import { isPlainObject } from "@ds/utils/type-guards/common-guards";

/** Shown for empty contact fields in read-only info + details views. */
export const CONTACT_FIELD_EMPTY_PLACEHOLDER = "\u2014";

export function contactFieldValueOrPlaceholder(value: string | undefined): string {
  return value?.trim() ? value.trim() : CONTACT_FIELD_EMPTY_PLACEHOLDER;
}

/**
 * APIs may return `contacts` as an array, a single object, or a numeric-key map (e.g. JSON / ORM shapes).
 * The normalizer used to treat non-arrays as `[]`, which wiped real contacts in Redux.
 * Array and map values are limited to plain objects; null, primitives, and malformed entries are dropped.
 */
export function coerceContactsToContactArray(value: unknown): LocationContact[] {
  if (value === null || value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(isPlainObject) as unknown as LocationContact[];
  }

  if (isPlainObject(value)) {
    const numericKeys = Object.keys(value).filter(k => /^\d+$/.test(k));
    if (numericKeys.length > 0) {
      return numericKeys
        .sort((a, b) => Number(a) - Number(b))
        .map(k => (value as Record<string, unknown>)[k])
        .filter(isPlainObject) as unknown as LocationContact[];
    }
    if ("name" in value || "email" in value || "phone" in value || "title" in value) {
      return [value as unknown as LocationContact];
    }
  }
  return [];
}

/** Read contacts from `Locality` or raw API shapes (`location_contacts`, nested `location`, etc.). */
export function getContactsArrayFromLocation(location: unknown): LocationContact[] | undefined {
  if (!location || typeof location !== "object") {
    return undefined;
  }
  const o = location as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(o, "contacts")) {
    return coerceContactsToContactArray(o.contacts);
  }
  if (Object.prototype.hasOwnProperty.call(o, "location_contacts")) {
    return coerceContactsToContactArray(o.location_contacts);
  }
  if (Object.prototype.hasOwnProperty.call(o, "location") && isPlainObject(o.location)) {
    return getContactsArrayFromLocation(o.location);
  }
  return undefined;
}

/**
 * Digits only; strips a leading US country code when the string is exactly 1 + NANP (11 digits).
 * Shared with contact phone validation — avoids mis-formatting 11-digit values as (155) 512-34567.
 */
export function normalizeNanpPhoneDigits(value: string): string {
  return value
    .trim()
    .replace(/[^\d]+/g, "")
    .replace(/^1(\d{10})$/, "$1");
}

export function getContactPhoneDisplayValue(phone: string): string {
  if (!phone || typeof phone !== "string") {
    return "";
  }

  const digits = normalizeNanpPhoneDigits(phone);
  if (!digits) {
    return "";
  }
  if (/^\d{10}$/.test(digits)) {
    return digits.replace(/^(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3");
  }
  return digits;
}

export function normalizeContactsForSubmit(contacts: unknown): LocationContact[] {
  if (!contacts || typeof contacts !== "object") {
    return [];
  }

  const raw = Array.isArray(contacts)
    ? contacts
    : Object.keys(contacts)
        .filter(k => /^\d+$/.test(k))
        .sort((a, b) => Number(a) - Number(b))
        .map(k => (contacts as Record<string, unknown>)[k]);

  return raw
    .filter((item): item is Record<string, unknown> => item !== null && item !== undefined && typeof item === "object")
    .map(item => ({
      email: typeof item.email === "string" ? item.email.trim() : "",
      name: typeof item.name === "string" ? item.name.trim() : "",
      phone: typeof item.phone === "string" ? normalizeNanpPhoneDigits(item.phone) : "",
      title: typeof item.title === "string" ? item.title.trim() : "",
    }));
}

export function mapLocationContactsForForm(contacts: unknown): LocationContact[] {
  const rows = coerceContactsToContactArray(contacts);

  return rows.map(
    (item): LocationContact => ({
      ...(item.id !== undefined && { id: item.id }),
      email: item.email || "",
      name: item.name || "",
      phone: item.phone ? getContactPhoneDisplayValue(item.phone) : "",
      title: item.title || "",
    }),
  );
}
